// FE-PINV-066 — <NewChooser>.
//
// Page-header "+ New" button with a dropdown chooser covering the
// three creation paths producers most often hit from the Pipeline
// surface, plus a Run-a-Playbook entry that mounts FE-PINV-074's
// modal in ``pick`` mode.
//
// Row order (top-to-bottom):
//   1. Prospect — default-focused on open; P shortcut.
//   2. Opportunity — O shortcut.
//   3. Import Accounts from CSV — links to FE-SCRM-048's wizard.
//   4. Run a Playbook — opens <ApplyPlaybookModal mode="pick">.
//
// Shortcut keys are scoped to the open dropdown so they don't
// collide with text inputs across the app. Closing any spawned
// modal returns focus to the + New button (a11y requirement).

import {
  useCallback, useEffect, useRef, useState,
} from "react";
import { Link } from "react-router-dom";

import { AddOpportunityModal } from "../AddOpportunity";
import { AddProspectModal } from "../Prospects";
import { ApplyPlaybookModal } from "../Playbook";
import type { TargetSearchResult } from "../components/TargetSearchPicker";


type ActiveModal = null | "prospect" | "opportunity" | "playbook";


export interface NewChooserProps {
  /** Plumbs FE-SCRM-054 sales-search results into ApplyPlaybookModal's
   *  pick mode. Host injects so this component stays free of
   *  API-client knowledge. */
  fetchOpportunitySearchResults: (
    q: string,
    restrictTo: readonly ("opportunity")[] | undefined,
  ) => Promise<TargetSearchResult[]>;
}


export function NewChooser({
  fetchOpportunitySearchResults,
}: NewChooserProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<ActiveModal>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const prospectRowRef = useRef<HTMLButtonElement>(null);

  const closeModal = useCallback(() => {
    setModal(null);
    // Focus return on modal close — a11y AC.
    triggerRef.current?.focus();
  }, []);

  const openModal = useCallback((m: ActiveModal) => {
    setOpen(false);
    setModal(m);
  }, []);

  // Auto-focus the Prospect row when the dropdown opens.
  useEffect(() => {
    if (open) prospectRowRef.current?.focus();
  }, [open]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        return;
      }
      if (e.key === "Enter") {
        // Native Enter-on-button → click is bypassed in some test
        // environments (happy-dom); dispatch explicitly so the
        // a11y AC holds regardless of host.
        const focused = document.activeElement;
        if (focused instanceof HTMLElement) {
          e.preventDefault();
          focused.click();
        }
        return;
      }
      // Shortcut keys ONLY when dropdown is open (host-level
      // listener is intentionally not used so text inputs
      // elsewhere don't get swallowed).
      const k = e.key.toLowerCase();
      if (k === "p") {
        e.preventDefault();
        openModal("prospect");
        return;
      }
      if (k === "o") {
        e.preventDefault();
        openModal("opportunity");
        return;
      }
    },
    [openModal],
  );

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md border border-border-default bg-teal px-3 py-1 text-sm font-medium text-white hover:bg-teal-pressed"
      >
        + New
      </button>

      {open ? (
        <div
          data-testid="new-chooser-dropdown"
          role="menu"
          tabIndex={-1}
          onKeyDown={onKeyDown}
          className="absolute right-0 top-full z-30 mt-1 w-72 rounded-md border border-border-default bg-raised shadow-overlay"
        >
          <button
            ref={prospectRowRef}
            type="button"
            role="menuitem"
            data-testid="new-chooser-row-prospect"
            onClick={() => openModal("prospect")}
            className="flex w-full items-center justify-between px-3 py-2 text-sm text-text-primary hover:bg-recessed"
          >
            <span className="flex items-center gap-2">
              Prospect
              <span className="rounded bg-teal-tint px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-text-primary">
                Default
              </span>
            </span>
            <kbd className="rounded border border-border-subtle px-1.5 text-[10px] uppercase text-text-tertiary">
              P
            </kbd>
          </button>
          <button
            type="button"
            role="menuitem"
            data-testid="new-chooser-row-opportunity"
            onClick={() => openModal("opportunity")}
            className="flex w-full items-center justify-between px-3 py-2 text-sm text-text-primary hover:bg-recessed"
          >
            Opportunity
            <kbd className="rounded border border-border-subtle px-1.5 text-[10px] uppercase text-text-tertiary">
              O
            </kbd>
          </button>
          <Link
            to="/dashboard/sales-crm/import"
            role="menuitem"
            data-testid="new-chooser-row-import"
            onClick={() => setOpen(false)}
            className="block w-full px-3 py-2 text-sm text-text-primary hover:bg-recessed"
          >
            Import Accounts from CSV
          </Link>
          <button
            type="button"
            role="menuitem"
            data-testid="new-chooser-row-playbook"
            onClick={() => openModal("playbook")}
            className="flex w-full items-center justify-between px-3 py-2 text-sm text-text-primary hover:bg-recessed"
          >
            Run a Playbook
          </button>
        </div>
      ) : null}

      <AddProspectModal
        open={modal === "prospect"}
        onClose={closeModal}
      />
      <AddOpportunityModal
        open={modal === "opportunity"}
        onClose={closeModal}
      />
      {modal === "playbook" ? (
        <ApplyPlaybookModal
          mode="pick"
          fetchSearchResults={fetchOpportunitySearchResults}
          onClose={closeModal}
          onSuccess={() => {
            // Modal handles its own success Toast + cache
            // invalidation via hooks.ts; the chooser just closes.
          }}
        />
      ) : null}
    </div>
  );
}
